/// Dedicated username editor opened from the profile row.
/// Port of mobile/lib/user/ui/change_username_screen.dart.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';

import { ApiError } from '@/app/network/apiClient';
import { appRoutes } from '@/app/router/routes';
import { useAuthStore } from '@/auth/handler/authStore';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { validateUsername, minUsernameLength, maxUsernameLength } from '@/user/domain/username';

export function ChangeUsernameScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const changeUsername = useAuthStore((s) => s.changeUsername);
  const isRestoring = useAuthStore((s) => s.isRestoring);

  const [username, setUsername] = useState(user.username);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setUsername(user.username);
  }, [user.username]);

  if (!isRestoring && user.isAnonymous) {
    return <Navigate to={appRoutes.auth} replace />;
  }

  const canSave = !saving && username.trim().length >= minUsernameLength;

  const saveUsername = async (): Promise<void> => {
    if (!canSave) return;
    const next = username.trim();
    setError(null);
    setSuccess(null);
    if (next === user.username) {
      setSuccess(t('profileUsernameUnchanged'));
      return;
    }
    const invalid = validateUsername(next);
    if (invalid === 'length') {
      setError(t('profileUsernameLength'));
      return;
    }
    if (invalid === 'chars') {
      setError(t('profileUsernameChars'));
      return;
    }
    setSaving(true);
    try {
      await changeUsername(next);
      setSuccess(t('profileUsernameSaved'));
      window.setTimeout(() => navigate(-1), 450);
    } catch (err) {
      setError(mapUsernameError(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageChrome title={t('profileChangeUsernameTitle')}>
      <div className="flex-1 overflow-y-auto px-6 pb-10 pt-6">
        <label className="mb-2 block leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
          {t('authUsername')}
        </label>
        <PixelContainer
          radius={3}
          pixelScale={2}
          padding="4px 12px"
          backgroundColor={colors.surfaceContainerHighest}
          borderColor={colors.onSurface}
        >
          <input
            autoFocus
            value={username}
            disabled={saving}
            maxLength={maxUsernameLength}
            onChange={(event) => {
              setUsername(event.target.value.slice(0, maxUsernameLength));
              setError(null);
              setSuccess(null);
            }}
            className="w-full bg-transparent outline-none"
            style={{
              fontSize: 22,
              color: colors.onSurface,
              lineHeight: 1.2,
              fontFamily: 'inherit',
              border: 'none',
              padding: '6px 0',
            }}
          />
        </PixelContainer>
        <p className="mt-2 leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.65 }}>
          {t('profileUsernameHint')}
        </p>
        {error && (
          <p className="mt-2 leading-tight" style={{ fontSize: 16, color: '#ef5350' }}>
            {error}
          </p>
        )}
        {success && (
          <p className="mt-2 leading-tight" style={{ fontSize: 16, color: '#00c853' }}>
            {success}
          </p>
        )}
        <PixelContainer
          radius={3}
          pixelScale={2}
          padding="12px 0"
          backgroundColor={canSave ? colors.onSurface : colors.surfaceContainerHighest}
          borderColor={canSave ? colors.onSurface : colors.outline}
          className="mt-3 w-full"
          onClick={canSave ? () => void saveUsername() : undefined}
        >
          <div className="flex justify-center" style={{ minHeight: 22 }}>
            {saving ? (
              <span
                className="inline-block h-[18px] w-[18px] animate-spin rounded-full border-2"
                style={{ borderColor: colors.surface, borderTopColor: 'transparent' }}
              />
            ) : (
              <span
                className="leading-none"
                style={{
                  fontSize: 22,
                  color: canSave ? colors.surface : colors.onSurface,
                  opacity: canSave ? 1 : 0.4,
                }}
              >
                {t('profileSaveUsername')}
              </span>
            )}
          </div>
        </PixelContainer>
      </div>
    </PageChrome>
  );
}

function mapUsernameError(error: unknown, t: (key: string) => string): string {
  const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
  const lower = message.toLowerCase();
  if (lower.includes('taken') || lower.includes('already')) return t('profileUsernameTaken');
  if (lower.includes('invalid')) return t('profileUsernameInvalid');
  return message;
}
